/* CR48 Phase 5 — live text preview for the custom hoodie and tee.
   Security rule: user text only ever reaches the page through textContent and input.value.
   Nothing here builds HTML from user input. */
(() => {
  const clean = (raw, max) => String(raw == null ? '' : raw)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u2028-\u202E\u2060-\u206F\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^\s+/, '')
    .slice(0, max);

  function setup(root) {
    if (root.dataset.nmReady) return;
    root.dataset.nmReady = '1';
    const max = Number(root.dataset.max) || 18;
    const input = root.querySelector('[data-nm-customiser-input]');
    const count = root.querySelector('[data-nm-customiser-count]');
    const preview = root.querySelector('[data-nm-customiser-preview]');
    const sectionId = root.dataset.sectionId;
    if (!input || !preview) return;

    const pct = (v, d) => { const n = Number(v); return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : d; };
    preview.style.top = `${pct(root.dataset.top, 30)}%`;
    preview.style.left = `${pct(root.dataset.left, 50)}%`;
    preview.style.width = `${pct(root.dataset.width, 40)}%`;
    // The font size is worked out from the print-area width (CSS), so 18 characters shrink to fit instead of being cut off.
    preview.style.setProperty('--nm-ct-width', `${pct(root.dataset.width, 40)}cqi`);
    if (/^#[0-9a-f]{3,8}$/i.test(root.dataset.colour || '')) preview.style.color = root.dataset.colour;

    // With a blank base image set on the block, the preview lives on that image and never moves.
    const stage = root.querySelector('[data-nm-customiser-stage]');
    // Otherwise anchor the preview on the active (first) gallery slide. The gallery re-renders on variant change.
    const anchor = () => {
      if (stage) return;
      const gallery = document.getElementById(`MediaGallery-${sectionId}`) || document.querySelector('media-gallery');
      const slide = gallery?.querySelector('.product__media-item.is-active .product-media-container, .product__media-item.is-active')
        || gallery?.querySelector('.product__media-item .product-media-container, .product__media-item');
      if (!slide) return;
      if (preview.parentElement !== slide) slide.appendChild(preview);
      slide.classList.add('nm-customiser__slide');
    };

    const render = () => {
      const value = clean(input.value, max);
      if (input.value !== value) input.value = value; // enforce the limit even if maxlength is bypassed
      const shown = value.toUpperCase();
      preview.textContent = shown; // textContent only: markup typed by a shopper stays literal text
      preview.hidden = shown.length === 0;
      if (count) count.textContent = `${value.length} of ${max} characters`;
      // Fit long text: shrink the font as characters are added so it stays inside the print area.
      preview.style.setProperty('--nm-ct-len', String(Math.max(shown.length, 4)));
    };

    // Uppercase what is stored, so the order matches the preview exactly.
    input.addEventListener('input', () => {
      const pos = input.selectionStart;
      input.value = clean(input.value, max).toUpperCase();
      try { input.setSelectionRange(pos, pos); } catch (e) { /* some input types disallow */ }
      render();
    });

    // CR48 P5: Sense renders the product form with novalidate and posts it from its own submit listener, so the
    // input's required/minlength never run and an EMPTY custom text could be added to the cart. The guards below
    // run in the capture phase on the document, before the form's and apps' own listeners.
    const productForm = input.form || (input.getAttribute('form') && document.getElementById(input.getAttribute('form')));
    const hasText = () => clean(input.value, max).trim().length >= 1;
    let errorEl = null;
    const clearError = () => {
      input.removeAttribute('aria-invalid');
      if (errorEl) { errorEl.remove(); errorEl = null; }
    };
    const showError = () => {
      input.setAttribute('aria-invalid', 'true');
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.className = 'nm-customiser__error';
        errorEl.id = `${input.id}-error`;
        errorEl.setAttribute('role', 'alert');
        input.insertAdjacentElement('afterend', errorEl);
        input.setAttribute('aria-describedby', `${input.getAttribute('aria-describedby') || ''} ${errorEl.id}`.trim());
      }
      errorEl.textContent = `Type your text first: 1 to ${max} characters.`;
      input.focus({ preventScroll: false });
    };

    // CR48 P5: Textify (an app) is still switched on for these products. It adds its own required text box outside
    // the form, copies it into hidden properties[CUSTOM TEXT] inputs, and blocks the Add to cart click while its box
    // is empty. With two boxes, a shopper who typed only in ours could not buy and was not told why (staging test,
    // 2026-09-15 04:54). Until Textify is switched off on these products (decision T1), copy our text into Textify's
    // fields and hide its duplicate box. Values only: nothing is written as HTML.
    const TEXTIFY = '[name="properties[CUSTOM TEXT]"]';
    const syncTextify = () => {
      const value = clean(input.value, max).toUpperCase();
      document.querySelectorAll(TEXTIFY).forEach((el) => {
        if (el === input) return;
        if (el.value !== value) {
          el.value = value;
          el.dispatchEvent(new Event('input', { bubbles: true }));
          el.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (el.tagName === 'TEXTAREA' && !el.dataset.nmMirrored) {
          el.dataset.nmMirrored = '1';
          const wrap = el.parentElement;
          const hideWrap = wrap && wrap !== document.body && !wrap.contains(input) && !wrap.querySelector('.product-form__submit')
            && wrap.querySelectorAll('input, textarea, select, button').length === 1;
          const target = hideWrap ? wrap : el;
          target.classList.add('nm-textify-mirrored');
          target.setAttribute('aria-hidden', 'true');
          el.tabIndex = -1;
          if (el.id) document.querySelectorAll(`label[for="${CSS.escape(el.id)}"]`).forEach((l) => l.classList.add('nm-textify-mirrored'));
          // CR49 C11: once every Textify field is mirrored, hide its whole container, including the label it leaves behind.
          const app = el.closest('#textfieldapp');
          if (app && [...app.querySelectorAll('textarea, input:not([type="hidden"]), select')].every((f) => f.dataset.nmMirrored)) {
            app.classList.add('nm-textify-mirrored');
            app.setAttribute('aria-hidden', 'true');
          }
        }
      });
    };
    // Textify's box can appear after this script runs (app scripts load late): sync when it shows up.
    new MutationObserver(() => {
      if (document.querySelector(`textarea${TEXTIFY}:not([data-nm-mirrored])`)) syncTextify();
    }).observe(document.body, { childList: true, subtree: true });

    if (productForm) {
      // 1) Add to cart click (also the sticky bar, which clicks this button): runs before Textify's click listeners.
      document.addEventListener('click', (evt) => {
        const btn = evt.target && evt.target.closest ? evt.target.closest('.product-form__submit') : null;
        if (!btn || btn.form !== productForm) return;
        if (hasText()) { syncTextify(); return; }
        evt.preventDefault();
        evt.stopImmediatePropagation();
        showError();
      }, true);
      // 2) Form submit from any other path (Enter key, requestSubmit): runs before Sense's submit listener.
      document.addEventListener('submit', (evt) => {
        if (evt.target !== productForm) return;
        if (hasText()) { syncTextify(); return; }
        evt.preventDefault();
        evt.stopImmediatePropagation();
        showError();
      }, true);
      input.addEventListener('input', () => { if (hasText()) clearError(); });
    }
    input.addEventListener('input', syncTextify);

    // CR49 C12: the help line, count and preview open the first time the field is focused or holds text, and then stay open.
    // Never closed again on blur: closing as the shopper taps Add to cart would move the button out from under their finger.
    const activate = () => { if (document.activeElement === input || input.value.length > 0) root.classList.add('is-active'); };
    input.addEventListener('focus', activate);
    input.addEventListener('input', activate);

    const fromUrl = new URLSearchParams(window.location.search).get('text');
    if (fromUrl && !input.value) input.value = clean(fromUrl, max).toUpperCase();

    anchor();
    render();
    syncTextify();
    activate();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => requestAnimationFrame(() => { anchor(); render(); }));
    }
    new MutationObserver(() => { if (!document.contains(preview) || !preview.closest('.product__media-item')) anchor(); })
      .observe(document.getElementById(`MediaGallery-${sectionId}`) || document.body, { childList: true, subtree: true });
  }

  const init = () => document.querySelectorAll('[data-nm-customiser]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
